/**
 * Utility per compressione e ridimensionamento immagini
 * 
 * Risolve il problema del blocco su Android quando si caricano foto dalla galleria
 * che possono essere molto grandi (8-12MB).
 */

export interface CompressionOptions {
  /** Dimensione massima in pixel del lato maggiore (default: 1920) */
  maxDimension?: number
  /** Qualità JPEG 0-1 (default: 0.8) */
  quality?: number
  /** Formato output (default: image/jpeg) */
  mimeType?: 'image/jpeg' | 'image/png' | 'image/webp'
  /** Dimensione massima in bytes per l'immagine compressa (default: 3MB) */
  maxSizeBytes?: number
}

export interface CompressionResult {
  blob: Blob
  originalSize: number
  compressedSize: number
  compressionRatio: number
  width: number
  height: number
}

const DEFAULT_OPTIONS: Required<CompressionOptions> = {
  maxDimension: 1920,
  quality: 0.8,
  mimeType: 'image/jpeg',
  maxSizeBytes: 3 * 1024 * 1024 // 3MB
}

/**
 * Comprimi e ridimensiona un'immagine da Blob
 */
export async function compressImage(
  inputBlob: Blob,
  options: CompressionOptions = {}
): Promise<CompressionResult> {
  const opts = { ...DEFAULT_OPTIONS, ...options }
  const originalSize = inputBlob.size
  
  // Se l'immagine è già piccola, ritorna così com'è
  if (originalSize <= opts.maxSizeBytes / 2) {
    return {
      blob: inputBlob,
      originalSize,
      compressedSize: originalSize,
      compressionRatio: 1,
      width: 0,
      height: 0
    }
  }
  
  // Carica l'immagine
  const imageBitmap = await createImageBitmap(inputBlob)
  
  // Calcola le nuove dimensioni mantenendo l'aspect ratio
  let { width, height } = imageBitmap
  
  if (width > opts.maxDimension || height > opts.maxDimension) {
    if (width > height) {
      height = Math.round(height * (opts.maxDimension / width))
      width = opts.maxDimension
    } else {
      width = Math.round(width * (opts.maxDimension / height))
      height = opts.maxDimension
    }
  }
  
  // Crea canvas per il ridimensionamento
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  
  const ctx = canvas.getContext('2d')
  if (!ctx) {
    throw new Error('Canvas context non disponibile')
  }
  
  // Disegna l'immagine ridimensionata
  ctx.drawImage(imageBitmap, 0, 0, width, height)
  imageBitmap.close() // Libera memoria
  
  // Compressione progressiva se ancora troppo grande
  let quality = opts.quality
  let resultBlob = await canvasToBlob(canvas, opts.mimeType, quality)
  
  // Se ancora troppo grande, riduci qualità progressivamente
  while (resultBlob.size > opts.maxSizeBytes && quality > 0.3) {
    quality -= 0.1
    resultBlob = await canvasToBlob(canvas, opts.mimeType, quality)
  }
  
  // Se ancora troppo grande, riduci ulteriormente le dimensioni
  if (resultBlob.size > opts.maxSizeBytes) {
    const scale = Math.sqrt(opts.maxSizeBytes / resultBlob.size)
    const newWidth = Math.round(width * scale)
    const newHeight = Math.round(height * scale)
    
    canvas.width = newWidth
    canvas.height = newHeight
    
    // Ricarica immagine originale per nuova compressione
    const tempBitmap = await createImageBitmap(inputBlob)
    ctx.drawImage(tempBitmap, 0, 0, newWidth, newHeight)
    tempBitmap.close()
    
    resultBlob = await canvasToBlob(canvas, opts.mimeType, 0.6)
    width = newWidth
    height = newHeight
  }
  
  return {
    blob: resultBlob,
    originalSize,
    compressedSize: resultBlob.size,
    compressionRatio: originalSize / resultBlob.size,
    width,
    height
  }
}

/**
 * Comprimi e ridimensiona un'immagine da File
 */
export async function compressFile(
  file: File,
  options: CompressionOptions = {}
): Promise<File> {
  // Salta se non è un'immagine
  if (!file.type.startsWith('image/')) {
    return file
  }
  
  const result = await compressImage(file, options)
  
  // Crea nuovo File con blob compresso
  const fileName = file.name.replace(/\.[^.]+$/, '.jpg')
  return new File([result.blob], fileName, {
    type: result.blob.type,
    lastModified: Date.now()
  })
}

/**
 * Comprimi un array di Blob
 */
export async function compressBlobs(
  blobs: Blob[],
  options: CompressionOptions = {},
  onProgress?: (current: number, total: number) => void
): Promise<Blob[]> {
  const results: Blob[] = []
  
  for (let i = 0; i < blobs.length; i++) {
    onProgress?.(i, blobs.length)
    const result = await compressImage(blobs[i], options)
    results.push(result.blob)
  }
  
  onProgress?.(blobs.length, blobs.length)
  return results
}

/**
 * Helper: canvas to blob
 */
function canvasToBlob(
  canvas: HTMLCanvasElement,
  mimeType: string,
  quality: number
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob)
        } else {
          reject(new Error('Conversione canvas a blob fallita'))
        }
      },
      mimeType,
      quality
    )
  })
}

/**
 * Verifica se un Blob necessita compressione
 */
export function needsCompression(
  blob: Blob,
  maxSizeBytes: number = DEFAULT_OPTIONS.maxSizeBytes
): boolean {
  return blob.size > maxSizeBytes
}

/**
 * Formatta dimensione file in formato leggibile
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}
